<template>
  <div>
    <b-overlay :show="loading" rounded="sm" opacity="0.6" no-fade>
      <b-card class="card_body_bottom">
        <template #header>
          <b-row class="flex-row justify-content-start align-items-center" style="width: 100%">
            <b-col cols="12" xl="6" lg="6" md="6" sm="12" class="d-flex justify-content-start align-items-center" style="gap:5px">
              <h4 class="mb-0">Intesa SetWeb Report</h4>
              <b-button
                variant="flat-secondary"
                class="btn-icon rounded-circle"
                :disabled="selectedAccounts.length === 0"
                @click="fetchData"
                v-b-tooltip.hover="'Refresh Report'"
              >
                <feather-icon icon="RefreshCcwIcon" />
              </b-button>
            </b-col>
          </b-row>
        </template>

        <b-card-body>
          <b-row class="align-items-end">
            <!-- Accounts -->
            <b-col cols="12" md="6" xl="4" lg="4" sm="12">
              <label class="compact_label">Accounts</label>
              <v-select
                class="compact_form_vue-multi-select"
                v-model="selectedAccounts"
                label="AccountName"
                :options="optAccounts"
                multiple
                :close-on-select="false"
                placeholder="Select account(s)"
              />
            </b-col>

            <!-- Select all accounts -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12" style="padding-bottom: 0.4rem;">
              <b-form-checkbox v-model="allAccountsSelected" @change="toggleAllAccounts">
                All Accounts
              </b-form-checkbox>
            </b-col>

            <!-- Start Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">Start Date</label>
              <b-form-datepicker
                v-model="startDate"
                placeholder="Start date"
                :start-weekday="1"
                :hide-header="true"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>

            <!-- End Date -->
            <b-col cols="12" md="6" xl="2" lg="2" sm="12">
              <label class="compact_label">End Date</label>
              <b-form-datepicker
                v-model="endDate"
                placeholder="End date"
                :start-weekday="1"
                :hide-header="true"
                :min="startDate"
                class="form-control"
                today-button
                reset-button
              />
            </b-col>
          </b-row>

          <b-row class="mt-1">
            <b-col cols="12" class="d-flex align-items-end" style="gap: 8px;">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                size="sm"
                :disabled="selectedAccounts.length === 0"
                @click="fetchData"
              >
                <feather-icon icon="SearchIcon" class="mr-50" />
                <span class="align-middle">Generate Report</span>
              </b-button>

              <b-button
                v-if="rows.length > 0"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-success"
                size="sm"
                :disabled="downloading"
                @click="downloadCsv"
              >
                <b-spinner v-if="downloading" small class="mr-50" />
                <feather-icon v-else icon="DownloadIcon" class="mr-50" />
                <span class="align-middle">Download CSV</span>
              </b-button>
            </b-col>
          </b-row>

          <b-row v-if="rows.length > 0" class="mt-50">
            <b-col cols="12">
              <small class="text-muted">
                Upload the CSV to SetWeb as downloaded. Opening it by
                double-clicking makes Excel read columns like COD_CTP and ORD_CC
                as numbers and hide leading zeros (0908 shown as 908) &mdash; the
                file itself is correct. To view it, use Excel's Data &gt; From
                Text/CSV and set those columns to Text.
              </small>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>

      <template #overlay v-if="loading">
        <div class="text-center d-flex justify-content-center align-items-start mt-3">
          <b-icon icon="stopwatch" font-scale="3" animation="cylon" />
          <p id="cancel-label">
            <feather-icon icon="TargetIcon" />
            Preparing report...
          </p>
        </div>
      </template>
    </b-overlay>

    <!-- Results Table -->
    <b-card v-if="rows.length > 0" class="mt-1">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <span class="text-muted" style="font-size: 0.8rem;">
          {{ rows.length }} trade(s)
        </span>
        <span class="text-muted" style="font-size: 0.75rem;">Scroll horizontally to view all columns</span>
      </div>
      <div class="table-responsive">
        <b-table
          class="compact_table intesa-report-table"
          :items="rows"
          :fields="tableColumns"
          :small="true"
          :hover="true"
          :striped="true"
          :bordered="true"
          :fixed="false"
          headVariant="dark"
          table-variant="none"
          responsive
          :empty-text="'No trades found for the selected criteria.'"
          show-empty
        >
          <template #cell(MOV_T)="data">
            <b-badge :variant="data.value === 'A' ? 'light-success' : 'light-danger'">
              {{ data.value }}
            </b-badge>
          </template>
          <template #cell(IMPORTO)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value) }}</div>
          </template>
          <template #cell(QUANT)="data">
            <div class="text-right text-nowrap">{{ formatNumber(data.value) }}</div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-alert v-else-if="searched && rows.length === 0" variant="primary" show class="mt-1">
      <div class="alert-body">
        <span>No trades found for the selected criteria.</span>
      </div>
    </b-alert>

    <!-- Missing custody details prompt -->
    <b-modal
      v-model="showCustodyModal"
      title="Missing Account Details"
      size="lg"
      ok-title="Download"
      cancel-title="Cancel"
      :ok-disabled="!custodiesComplete"
      @ok="confirmCustodies"
    >
      <p class="text-muted" style="font-size: 0.85rem;">
        These securities need a manual answer before downloading: the client
        account's settlement venue couldn't be worked out automatically, the
        account has no safekeeping number recorded at that venue, or SFC Prop
        Accounts has no ID Schema configured for the resolved venue pair.
        Picking a venue fills its safekeeping number in from Account Creation;
        anything still empty has to be typed in.
      </p>
      <div
        v-for="entry in missingCustodies"
        :key="entry.CustomerId + '-' + entry.Isin"
        class="border-bottom pb-50 mb-50"
      >
        <b-row class="align-items-end">
          <b-col cols="12" md="3">
            <label class="compact_label">Account</label>
            <div style="font-size: 0.85rem;">{{ entry.AccountName }}</div>
          </b-col>
          <b-col cols="12" md="3">
            <label class="compact_label">Security</label>
            <div style="font-size: 0.85rem;">
              {{ entry.SecurityTicker || '-' }}
              <span class="text-muted">({{ entry.Isin }})</span>
            </div>
          </b-col>

          <b-col cols="12" md="3">
            <label class="compact_label">Client Settlement Venue</label>
            <b-form-select
              v-if="entry.NeedsClientPlace"
              v-model="entry.PlaceOfSettlement"
              :options="placeOptionsFor(entry)"
              size="sm"
              @change="onVenueSelected(entry, $event)"
            />
            <div v-else style="font-size: 0.85rem;">{{ entry.ClientPlace }}</div>
          </b-col>
          <b-col
            v-if="entry.NeedsClientPlace || entry.NeedsSafekeeping"
            cols="12"
            md="3"
          >
            <label class="compact_label">Safekeeping Number (SAFE_BRK)</label>
            <b-form-input
              v-model="entry.AccountNumber"
              size="sm"
              type="text"
              :readonly="isSafekeepingAutoFilled(entry)"
              :placeholder="entry.NeedsClientPlace && !entry.PlaceOfSettlement ? 'Select a venue first' : ''"
            />
            <small v-if="isSafekeepingAutoFilled(entry)" class="text-muted">
              From Account Creation
            </small>
          </b-col>
          <b-col v-if="entry.NeedsIdSchema" cols="12" md="3">
            <label class="compact_label">Account ID Schema (COD_CTP)</label>
            <b-form-input v-model="entry.IdSchema" size="sm" type="text" />
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
  BTable, BOverlay, BIcon, BAlert, BSpinner, BBadge, VBTooltip,
  BModal, BFormSelect, BFormInput,
} from 'bootstrap-vue';
import store from '@/store';
import { onUnmounted } from '@vue/composition-api';
import vSelect from 'vue-select';
import intesaReportStoreModule from './intesaReportStoreModule';
import settingsStoreModule from '../../settings/settingsStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import Ripple from 'vue-ripple-directive';

const STORE_MODULE = 'intesaReport';
const SETTINGS_STORE_MODULE = 'settings';

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BFormDatepicker, BFormCheckbox,
    BTable, BOverlay, BIcon, BAlert, BSpinner, BBadge, vSelect, ToastificationContent,
    BModal, BFormSelect, BFormInput,
  },

  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },

  setup() {
    if (!store.hasModule(STORE_MODULE)) {
      store.registerModule(STORE_MODULE, intesaReportStoreModule);
    }
    if (!store.hasModule(SETTINGS_STORE_MODULE)) {
      store.registerModule(SETTINGS_STORE_MODULE, settingsStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE)) store.unregisterModule(STORE_MODULE);
      if (store.hasModule(SETTINGS_STORE_MODULE)) store.unregisterModule(SETTINGS_STORE_MODULE);
    });
  },

  data() {
    return {
      selectedAccounts: [],
      optAccounts: [],
      allAccountsSelected: false,
      startDate: null,
      endDate: null,
      rows: [],
      loading: false,
      downloading: false,
      searched: false,
      showCustodyModal: false,
      missingCustodies: [],
      optVenues: [],
    };
  },

  computed: {
    tableColumns() {
      // render every CSV column; the first ones get a bit more room
      const wide = {
        CONTR: 170, ISIN_COD: 130, CONTR_DT: 110, VAL_DT: 110,
        PSET_CTP: 130, BRK_SWIFT: 130, SAFE_BRK: 150, TP_COD_CTP: 120,
        COD_CTP: 100, IMPORTO: 120, QUANT: 100,
      };
      if (this.rows.length === 0) return [];
      return Object.keys(this.rows[0]).map((key) => ({
        key,
        label: key,
        thStyle: { minWidth: (wide[key] || 90) + 'px' },
      }));
    },

    queryParams() {
      return {
        accounts: this.selectedAccounts.map((a) => a.AccountName),
        dateStart: this.startDate,
        dateEnd: this.endDate,
      };
    },

    custodiesComplete() {
      const filled = (v) => v !== null && v !== undefined && String(v).trim() !== '';

      return this.missingCustodies.every((m) => {
        if (m.NeedsClientPlace && !m.PlaceOfSettlement) return false;
        if ((m.NeedsClientPlace || m.NeedsSafekeeping) && !filled(m.AccountNumber)) return false;
        if (m.NeedsIdSchema && !filled(m.IdSchema)) return false;
        return true;
      });
    },

    venueNames() {
      return this.optVenues.reduce((acc, v) => {
        acc[String(v.Abbreviation).toUpperCase()] = v.Name;
        return acc;
      }, {});
    },
  },

  mounted() {
    this.getAccounts();
    this.getVenues();
  },

  methods: {
    getAccounts() {
      store
        .dispatch('intesaReport/getIntesaAccounts')
        .then((res) => {
          this.optAccounts = res.data.resData || [];
        })
        .catch((e) => {
          this.showError(`Error fetching accounts: ${e.message}`);
        });
    },

    getVenues() {
      store
        .dispatch('settings/getVenues')
        .then((res) => {
          this.optVenues = res.data || [];
        })
        .catch((e) => {
          this.showError(`Error fetching venues: ${e.message}`);
        });
    },

    /**
     * Venues offered for a row: the ones this account actually has on Account
     * Creation, so the safekeeping number can come with the choice. Accounts
     * with none configured still get the full venue list to pick from.
     */
    placeOptionsFor(entry) {
      const configured = entry.ClientVenues || [];
      const label = (place) => (this.venueNames[place] ? `${place} (${this.venueNames[place]})` : place);

      const options = configured.length > 0
        ? configured.map((v) => ({ value: v.PlaceOfSettlement, text: label(v.PlaceOfSettlement) }))
        : this.optVenues.map((v) => ({ value: v.Abbreviation, text: label(String(v.Abbreviation).toUpperCase()) }));

      return [{ value: null, text: 'Select venue' }, ...options];
    },

    /** Safekeeping number recorded for this account at the given venue. */
    venueSafekeeping(entry, place) {
      if (!place) return '';
      const match = (entry.ClientVenues || []).find(
        (v) => v.PlaceOfSettlement === String(place).toUpperCase()
      );
      return match ? match.SafekeepingAccount || '' : '';
    },

    onVenueSelected(entry, place) {
      // pull SAFE_BRK straight from the account's own venue details; only
      // venues with nothing recorded leave the field to be typed in
      this.$set(entry, 'AccountNumber', this.venueSafekeeping(entry, place));
    },

    isSafekeepingAutoFilled(entry) {
      return entry.NeedsClientPlace
        && !!entry.PlaceOfSettlement
        && this.venueSafekeeping(entry, entry.PlaceOfSettlement) !== '';
    },

    toggleAllAccounts(checked) {
      this.selectedAccounts = checked ? [...this.optAccounts] : [];
    },

    fetchData() {
      if (this.selectedAccounts.length === 0) return;
      this.loading = true;
      this.searched = false;

      store
        .dispatch('intesaReport/getIntesaReportData', this.queryParams)
        .then((res) => {
          this.rows = res.data.data || [];
          this.searched = true;
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.searched = true;
          this.showError(`Error generating report: ${e.message}`);
        });
    },

    downloadCsv() {
      if (this.selectedAccounts.length === 0) return;
      this.downloading = true;

      store
        .dispatch('intesaReport/checkIntesaCustodies', this.queryParams)
        .then((res) => {
          const missing = res.data.data || [];
          if (missing.length === 0) {
            this.performDownload([]);
            return;
          }
          this.downloading = false;
          this.missingCustodies = missing.map((m) => ({
            ...m,
            PlaceOfSettlement: null,
            AccountNumber: null,
            IdSchema: null,
          }));
          this.showCustodyModal = true;
        })
        .catch((e) => {
          this.downloading = false;
          this.showError(`Error checking account details: ${e.message}`);
        });
    },

    confirmCustodies() {
      if (!this.custodiesComplete) return;
      this.downloading = true;
      this.performDownload(
        this.missingCustodies.map((m) => ({
          CustomerId: m.CustomerId,
          Isin: m.Isin,
          PlaceOfSettlement: m.PlaceOfSettlement,
          AccountNumber: m.AccountNumber,
          IdSchema: m.IdSchema,
        }))
      );
    },

    performDownload(custodyOverrides) {
      store
        .dispatch('intesaReport/downloadIntesaReportCsv', {
          ...this.queryParams,
          custodyOverrides,
        })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          const now = new Date();
          const dmy = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;
          link.setAttribute('download', `MultiploTitoli_${dmy}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.downloading = false;
        })
        .catch((e) => {
          this.downloading = false;
          this.showError(`Error downloading report: ${e.message}`);
        });
    },

    formatNumber(value) {
      if (value === null || value === '' || value === undefined) return '';
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    showError(msg) {
      this.$toast({
        component: ToastificationContent,
        props: { title: 'Error', text: msg, icon: 'AlertTriangleIcon', variant: 'danger' },
      });
    },
  },

  watch: {
    selectedAccounts(val) {
      this.allAccountsSelected = this.optAccounts.length > 0 && val.length === this.optAccounts.length;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/src/assets/scss/compact-design.scss";

.intesa-report-table {
  font-size: 0.72rem;
}

.card::v-deep .card-body {
  padding: 0.8rem 0.7rem !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
